import { useTranslation } from "react-i18next";
import { AppButton } from "@/components/ui";
import { Github, Loader2 } from "lucide-react";
import type { OAuthProvider } from "../../types/auth";
import { OAUTH_PROVIDERS, SUPPORTED_OAUTH_PROVIDERS } from "../../types/auth";
import { GoogleIcon, GitLabIcon, BitbucketIcon } from "./icons";

interface OAuthButtonsProps {
  oauthConnecting: OAuthProvider | null;
  isDisabled: boolean;
  onOAuthClick: (provider: OAuthProvider) => void;
}

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  github: Github,
  google: GoogleIcon,
  gitlab: GitLabIcon,
  bitbucket: BitbucketIcon,
};

export function OAuthButtons({
  oauthConnecting,
  isDisabled,
  onOAuthClick,
}: OAuthButtonsProps): React.ReactElement {
  const { t } = useTranslation();

  function handleClick(provider: OAuthProvider) {
    // Block clicks during connecting
    if (oauthConnecting !== null) return;
    // No-op for unsupported providers
    if (!SUPPORTED_OAUTH_PROVIDERS.includes(provider)) return;
    onOAuthClick(provider);
  }

  return (
    <div className="grid grid-cols-2 gap-4">
      {OAUTH_PROVIDERS.map((config) => {
        const Icon = ICON_MAP[config.icon];
        const isConnecting = oauthConnecting === config.id;
        const allDisabled = isDisabled || oauthConnecting !== null;

        return (
          <AppButton
            key={config.id}
            variant="ghost"
            className="flex h-auto w-full items-center justify-center gap-3 rounded-2xl border border-border bg-surface py-4 text-sm font-semibold text-foreground hover:bg-background hover:shadow-sm transition-all"
            isDisabled={allDisabled}
            aria-label={t("auth.oauth.providerAriaLabel", {
              provider: config.name,
            })}
            onPress={() => handleClick(config.id)}
          >
            {isConnecting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>{t("auth.oauth.connecting")}</span>
              </>
            ) : (
              <>
                {Icon && <Icon className="h-4 w-4" />}
                <span>{config.name}</span>
              </>
            )}
          </AppButton>
        );
      })}
    </div>
  );
}
